import { useMemo, useState, type ReactNode } from 'react';
import { BryqSection } from './BryqSection';
import { Accordion } from '@finn-auto/design-system/atoms/accordion';
import type { RoleBoard, SlaActNowItem } from '../../types/taActionBoard';
import type { ExpiredInvite } from '../../services/interviewLinksApi';
import { COLORS } from './format';
import { RoleAccordion } from './RoleAccordion';
import { ActNowCard, AwaitingCard, Dot, SlaCard, SnoozeButton, SnoozedCard } from './SlaCards';
import type { BoardDestination } from './slaChipTarget';
import { ExpiredLinkRows, ExpiredLinksSection } from './ExpiredLinksSection';
import { useExpiredLinks } from './useExpiredLinks';
import type { PendingAction } from './usePendingActions';
import { InviteResponsesBlock } from './InviteResponsesSection';
import { SlaSnoozeModal, type SnoozeSubmit } from './SlaSnoozeModal';
import { releaseSlaSnooze, snoozeSlaCandidate } from '../../services/taSlaSnoozeApi';
import { RolePipeline } from './RolePipeline';

interface SlaTabProps {
  roles: RoleBoard[];
  /** opportunityId → "Archived" … — cards render muted across ALL tabs. */
  handled: Record<string, string>;
  /**
   * Take the recruiter to where this candidate's next action is done — the Feedback tab, or the
   * scheduling round they sit in. Every actionable stuck chip is a link to it (slaChipTarget).
   */
  onGoTo: (dest: BoardDestination, postingId: string, opportunityId: string) => void;
  /** Opens the confirm-then-DM flow for an interviewer who owes feedback. */
  onNudgeFeedback?: (role: RoleBoard, item: SlaActNowItem) => void;
  /** Whose board this is — scopes the expired-link nudge to their own roles, and
   * is the boardEmail a snooze is written against. */
  owner?: string;
  /** Reload the board after a snooze/un-snooze so the card moves group. */
  onChanged?: () => void;
  /** Queue an outbound email behind the board's ten-second countdown. */
  onDefer?: (action: Omit<PendingAction, 'secondsLeft'>) => void;
}

/** The candidate a snooze modal is open for. */
interface SnoozeTarget {
  opportunityId: string;
  name: string;
  /** Deep-link to the candidate in Lever, for the "also snooze in Lever" option. */
  leverUrl: string;
}

/**
 * Role header chip: the overdue count in red, or — when nothing is overdue — the
 * amber "invited" count, or a muted "snoozed" count for a role whose only open
 * items are parked. Without the last branch a snoozed-only role showed a red
 * "0 overdue", which reads as a breach when nothing is actually due.
 */
function roleChip(role: RoleBoard, expiredCount = 0) {
  const overSla = role.sla.actNow.length + role.sla.screeningOverSla;
  // An expired link is the loudest thing a collapsed card can say: the candidate
  // is holding a URL that does nothing, and nobody is waiting on us to decide.
  // It used to be invisible from here, which is how one got missed entirely.
  if (overSla === 0 && expiredCount > 0) {
    return {
      label: `${expiredCount} link${expiredCount === 1 ? '' : 's'} expired`,
      bg: COLORS.orangeBg,
      text: COLORS.orangeText,
    };
  }
  if (overSla === 0 && role.sla.awaitingCandidate.length > 0) {
    return {
      label: `${role.sla.awaitingCandidate.length} invited`,
      bg: COLORS.orangeBg,
      text: COLORS.orangeText,
    };
  }
  if (overSla === 0 && role.sla.snoozed.length > 0) {
    return { label: `${role.sla.snoozed.length} snoozed`, bg: COLORS.cotton, text: '#484848' };
  }
  return { label: `${overSla} overdue`, bg: COLORS.redBg, text: COLORS.redText };
}

/**
 * Invited candidates count as open work for the role too (they need chasing);
 * snoozed ones keep the role expandable so its snooze list stays reachable.
 */
function roleHasSlaItems(role: RoleBoard, expiredCount = 0): boolean {
  return (
    role.sla.actNow.length > 0 ||
    role.sla.screeningOverSla > 0 ||
    role.sla.awaitingCandidate.length > 0 ||
    role.sla.snoozed.length > 0 ||
    // Otherwise a role whose ONLY open item is an expired link renders collapsed
    // and unexpandable — which is precisely the candidate that gets missed.
    expiredCount > 0
  );
}

/** Overdue — everything waiting longer than 48h. */
export function SlaTab({
  roles,
  handled,
  onGoTo,
  onNudgeFeedback,
  owner,
  onChanged,
  onDefer,
}: SlaTabProps) {
  const postingIds = useMemo(() => roles.map((r) => r.postingId), [roles]);
  const expired = useExpiredLinks(owner, postingIds);
  const expiredFor = (role: RoleBoard) => expired.byPosting.get(role.postingId) ?? [];
  const firstOpen = roles.find((r) => roleHasSlaItems(r, expiredFor(r).length))?.postingId;
  const [open, setOpen] = useState<string[]>(firstOpen ? [firstOpen] : []);
  const [snoozeTarget, setSnoozeTarget] = useState<SnoozeTarget | null>(null);

  // A snooze is written against a board (owner); without one we cannot target a
  // board's cache, so the snooze affordances only appear on a real board.
  const canSnooze = Boolean(owner);
  const onSnooze = canSnooze
    ? (opportunityId: string, name: string, leverUrl: string) =>
        setSnoozeTarget({ opportunityId, name, leverUrl })
    : undefined;
  const onReleaseSnooze = canSnooze
    ? async (opportunityId: string) => {
        await releaseSlaSnooze(opportunityId, owner as string);
        onChanged?.();
      }
    : undefined;
  const confirmSnooze = async (input: SnoozeSubmit) => {
    if (!snoozeTarget || !owner) return;
    await snoozeSlaCandidate({ opportunityId: snoozeTarget.opportunityId, boardEmail: owner, ...input });
    onChanged?.();
  };

  return (
    <div className="space-y-4">
      <h2 className="text-base font-semibold text-gray-900">Waiting longer than 48 h</h2>

      {/* Expired links now sit INSIDE the role card they belong to (see
          useExpiredLinks). Only the ones matching no card are left out here. */}
      <ExpiredLinksSection items={expired.orphans} onSpent={expired.refetch} onDefer={onDefer} />

      {/* An interviewer who declined, or never answered, is the same outcome as
          an expired link — an interview that will not happen — so it belongs
          beside it rather than inside a role accordion. Only on a real board:
          the check is scoped to interviews this owner booked. */}
      {owner && <InviteResponsesBlock boardEmail={owner} />}

      <Accordion type="multiple" value={open} onValueChange={setOpen} className="space-y-3">
        {roles.map((role) => (
          <RoleAccordion
            key={role.postingId}
            role={role}
            hasOpenItems={roleHasSlaItems(role, expiredFor(role).length)}
            chip={roleChip(role, expiredFor(role).length)}
          >
            <div className="space-y-3">
            <RoleSlaSection
              role={role}
              handled={handled}
              expiredLinks={expiredFor(role)}
              onLinkResent={expired.refetch}
              onDefer={onDefer}
              onGoTo={onGoTo}
              onNudgeFeedback={onNudgeFeedback}
              onSnooze={onSnooze}
              onReleaseSnooze={onReleaseSnooze}
            />
            {/* The rest of this role's pipeline — see rolePipeline.ts. */}
            <RolePipeline role={role} boardEmail={owner || ''} onChanged={onChanged} />
            </div>
          </RoleAccordion>
        ))}
      </Accordion>

      {/* Bryq results to act on and tests still outstanding, below the roles and collapsible
          (Theresa, Oct 20 2026) — see BryqSection. */}
      <BryqSection />

      {snoozeTarget && (
        <SlaSnoozeModal
          candidateName={snoozeTarget.name}
          leverUrl={snoozeTarget.leverUrl}
          onClose={() => setSnoozeTarget(null)}
          onConfirm={confirmSnooze}
        />
      )}

      <p className="text-xs text-gray-400">
        Waiting = time since the last activity in Lever · overdue means longer than 48h · booked
        candidates are summarized, not listed · invited candidates (times proposed, no reply yet)
        and snoozed candidates are listed separately and never count as overdue.
      </p>
    </div>
  );
}

function RoleSlaSection({
  role,
  handled,
  expiredLinks = [],
  onLinkResent,
  onDefer,
  onGoTo,
  onNudgeFeedback,
  onSnooze,
  onReleaseSnooze,
}: {
  role: RoleBoard;
  handled: Record<string, string>;
  /** This role's expired, never-booked interview links — see useExpiredLinks. */
  expiredLinks?: ExpiredInvite[];
  onLinkResent?: () => void;
  onDefer?: (action: Omit<PendingAction, 'secondsLeft'>) => void;
  onGoTo: (dest: BoardDestination, postingId: string, opportunityId: string) => void;
  onNudgeFeedback?: (role: RoleBoard, item: SlaActNowItem) => void;
  onSnooze?: (opportunityId: string, name: string, leverUrl: string) => void;
  onReleaseSnooze?: (opportunityId: string) => Promise<void> | void;
}) {
  const { sla } = role;
  return (
    <div className="space-y-3">
      {/* FIRST in the card: a dead link is the one item here where the candidate
          is stuck and cannot act, so it outranks everything waiting on us. */}
      <ExpiredLinkRows
        items={expiredLinks}
        roleTitle={role.roleTitle}
        onSpent={onLinkResent}
        onDefer={onDefer}
      />
      {sla.newTotal > 0 && (
        <div>
          {/* clickable: opens Lever filtered to this role's NEW-applicant stage */}
          <a
            href={role.leverNewStageUrl}
            target="_blank"
            rel="noreferrer"
            className="text-xs font-medium text-blue-600 hover:underline"
          >
            {sla.newTotal} new in screening ↗
          </a>
        </div>
      )}

      {sla.actNow.length > 0 && (
        <div className="space-y-2 rounded-lg p-3" style={{ backgroundColor: COLORS.cotton }}>
          {sla.actNow.map((item) => (
            <ActNowCard
              key={item.opportunityId}
              role={role}
              item={item}
              handledLabel={handled[item.opportunityId] || null}
              onGoTo={onGoTo}
              onNudgeFeedback={onNudgeFeedback}
              onSnooze={onSnooze}
            />
          ))}
        </div>
      )}

      {/* The screening candidates behind the red "N over SLA" chip. Without
          this list a flagged role expanded to nothing but the summary chips
          above, which read as a dead click. */}
      {(sla.screeningActNow?.length ?? 0) > 0 && (
        <SlaSubsection heading="Screening · waiting over 48h">
          {(sla.screeningActNow ?? []).map((item) => (
            <SlaCard
              key={item.opportunityId}
              handledLabel={null}
              dot={<Dot color={item.waitingDays >= 5 ? COLORS.statusRed : COLORS.statusOrange} />}
              name={item.name}
              leverUrl={item.leverUrl}
              stageName={item.stageName}
              stuck={
                <span className="text-xs text-gray-500">waiting for a first screening decision</span>
              }
              actions={
                <>
                  {onSnooze && (
                    <SnoozeButton onClick={() => onSnooze(item.opportunityId, item.name, item.leverUrl)} />
                  )}
                  <span className="text-xs font-semibold" style={{ color: COLORS.redText }}>
                    {item.waitingDays.toFixed(1)}d
                  </span>
                </>
              }
            />
          ))}
          {sla.screeningListCapped && (
            <p className="text-xs text-gray-400">
              Showing the 25 longest waits of {sla.screeningOverSla} — open the role in Lever for the
              rest.
            </p>
          )}
        </SlaSubsection>
      )}

      {/* Invited candidates are NOT part of the act-now list: the ball is in
          their court, our SLA clock stopped when we sent the invitation. */}
      {sla.awaitingCandidate.length > 0 && (
        <SlaSubsection heading="Invited · awaiting candidate reply">
          {sla.awaitingCandidate.map((item) => (
            <AwaitingCard
              key={item.opportunityId}
              role={role}
              item={item}
              onGoTo={onGoTo}
            />
          ))}
        </SlaSubsection>
      )}

      {/* Board snoozes — parked on purpose with a reason, kept visible but muted
          and un-snoozable here. Lever-native snoozes are hidden from the board
          entirely (they can only be lifted in Lever), so they never reach here. */}
      {sla.snoozed.length > 0 && (
        <SlaSubsection heading="Snoozed" muted>
          {sla.snoozed.map((item) => (
            <SnoozedCard
              key={item.opportunityId}
              item={item}
              onReleaseSnooze={onReleaseSnooze}
            />
          ))}
        </SlaSubsection>
      )}
    </div>
  );
}

function SlaSubsection({
  heading,
  muted = false,
  children,
}: {
  heading: string;
  muted?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={muted ? 'space-y-2 opacity-70' : 'space-y-2'}>
      <div className="text-[11px] font-semibold tracking-wide text-gray-400">{heading}</div>
      <div className="space-y-2 rounded-lg p-3" style={{ backgroundColor: COLORS.cotton }}>
        {children}
      </div>
    </div>
  );
}
